import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import CategoryService from "services/CategoryService";

// Initial state for categories slice
const initialState = {
  loading: false,
  categories: [],
  category: { en: null, ar: null },
  successMessage: "",
  errorMessage: "",
};

// Async thunk to fetch categories
export const getCategories = createAsyncThunk(
  "categories/getCategories",
  async ({ languageId }, { rejectWithValue }) => {
    try {
      const response = await CategoryService.getCategories(languageId); // Pass languageId here
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching categories");
    }
  }
);

// Async thunk to fetch a single category

export const getCategory = createAsyncThunk(
  "categories/getCategory",
  async ({ categoryId }, { rejectWithValue }) => {
    try {
      const responseEn = await CategoryService.getCategory(categoryId, 1); // English
      const responseAr = await CategoryService.getCategory(categoryId, 4); // Arabic

      return { en: responseEn, ar: responseAr }; // Return both languages
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching category");
    }
  }
);

export const postCategories = createAsyncThunk(
  "categories/postCategories",
  async (params, { rejectWithValue }) => {
    try {
      const response = await CategoryService.createCategories(params);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);
// Async thunk to fetch categories
export const updateCategories = createAsyncThunk(
  "categories/updateCategories",
  async ({ categoryId, data }, { rejectWithValue }) => {
    try {
      const response = await CategoryService.updateCategories(categoryId, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error updating category");
    }
  }
);

// Async thunk to delete a category
export const deleteCategory = createAsyncThunk(
  "categories/deleteCategory",
  async ({ categoryId, languageId }, { rejectWithValue, dispatch }) => {
    try {
      // Call the deleteCategory API
      await CategoryService.deleteCategory(categoryId);

      // Fetch updated categories with the correct languageId after successful deletion
      await dispatch(getCategories({ languageId }));

      return { categoryId, message: "Category deleted successfully" };
    } catch (err) {
      // Handle error case
      return rejectWithValue(err.message || "Error deleting category");
    }
  }
);

// Create the slice for categories
const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getCategories lifecycle
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.categories = payload;
        state.showMessage = false;
      })
      .addCase(getCategories.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Handle getCategory lifecycle
      // Handle getCategory lifecycle
      .addCase(getCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategory.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.category = {
          en: payload.en, // English category data
          ar: payload.ar, // Arabic category data
        };
        state.showMessage = false;
      })
      .addCase(getCategory.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      .addCase(postCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(postCategories.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "Category created successfully!";
      })
      .addCase(postCategories.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      // Handle updateCategories lifecycle
      .addCase(updateCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCategories.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "Category updated successfully!";
      })
      .addCase(updateCategories.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      // Handle deleteCategory lifecycle
      .addCase(deleteCategory.fulfilled, (state, { payload }) => {
        // Remove the deleted category from the categories list
        state.categories = state.categories.filter(
          (category) => category.category_id !== payload.categoryId
        );
        state.message = payload.message; // Set success message
        state.showMessage = true;
      })
      .addCase(deleteCategory.rejected, (state, { payload }) => {
        state.message = payload;
        state.showMessage = true;
      });
  },
});

// Export the actions and reducer
export const { clearMessage } = categorySlice.actions;
export default categorySlice.reducer;
